import { useState, useRef, useCallback } from "react";
import {
  Link2,
  Copy,
  Check,
  AlertCircle,
  Settings,
  Users,
  X,
  QrCode,
  Image,
  Pencil,
} from "lucide-react";
import type { InviterType, PosterStatus } from "@invitation-system/shared";
import { setInviterLimit, setSuccessQr, updateInviter } from "../api/client";
import { QrCodeDialog } from "./QrCodeDialog";
import { PosterSection } from "./PosterSection";

interface GeneratedUrlCardProps {
  inviterId: string;
  url: string;
  displayName: string;
  createdAt?: string;
  highlighted?: boolean;
  count: number;
  maxCount?: number;
  successQrUrl?: string;
  note?: string;
  type?: InviterType;
  posterUrl?: string;
  posterStatus?: PosterStatus;
  onLimitChanged?: () => void;
}

type CopyState = "idle" | "copied" | "failed";

export function GeneratedUrlCard({
  inviterId,
  url,
  displayName,
  createdAt,
  highlighted,
  count,
  maxCount,
  successQrUrl,
  note,
  type,
  posterUrl,
  posterStatus,
  onLimitChanged,
}: GeneratedUrlCardProps) {
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [showSuccessQrDialog, setShowSuccessQrDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const urlRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
      timerRef.current = setTimeout(() => {
        setCopyState("idle");
        timerRef.current = null;
      }, 2000);
    } catch {
      setCopyState("failed");
      if (urlRef.current) {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(urlRef.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }, [url]);

  const limitReached = maxCount !== undefined && count >= maxCount;

  const cardClasses = [
    "rounded-xl bg-surface p-5 transition-shadow duration-200",
    highlighted
      ? "border-2 border-primary shadow-md"
      : limitReached
      ? "border border-warning shadow-sm"
      : "border border-border shadow-sm hover:shadow-md",
  ].join(" ");

  return (
    <>
      <div className={cardClasses}>
        <div className="flex items-start gap-4">
          <div
            className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              limitReached ? "bg-warning-light" : "bg-success-light"
            }`}
          >
            <Link2
              className={`h-5 w-5 ${limitReached ? "text-warning" : "text-accent-dark"}`}
              aria-hidden="true"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-base font-semibold text-foreground">{displayName}</p>
              <button
                type="button"
                onClick={() => setShowEditDialog(true)}
                aria-label={`编辑 ${displayName}`}
                className="cursor-pointer text-muted hover:text-primary transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              {createdAt && (
                <span className="text-xs text-muted">{formatRelativeDate(createdAt)}</span>
              )}
              {highlighted && (
                <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                  新生成
                </span>
              )}
              {limitReached && (
                <span className="text-xs font-medium text-warning bg-warning-light px-2 py-0.5 rounded">
                  已达上限
                </span>
              )}
            </div>

            {/* Note */}
            {note && (
              <p className="mt-1 text-xs text-muted italic">备注：{note}</p>
            )}

            <span
              ref={urlRef}
              className="mt-1.5 block text-sm text-primary break-all select-all font-mono"
              title={url}
            >
              {url}
            </span>

            {/* Count and limit info */}
            <div className="mt-2 flex items-center gap-1.5 text-xs text-muted">
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              <span>
                已报名 <span className="font-semibold text-foreground">{count}</span>
                {maxCount !== undefined ? (
                  <> / {maxCount} 人</>
                ) : (
                  <> 人 · 不限制</>
                )}
              </span>
            </div>

            {copyState === "failed" && (
              <p role="alert" className="mt-3 flex items-center gap-1.5 text-xs text-warning">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                自动复制失败，请手动选中链接后复制。
              </p>
            )}

            {/* Poster section for sponsor/speaker inviters */}
            {(type === "sponsor" || type === "speaker") && (
              <PosterSection
                inviterId={inviterId}
                posterUrl={posterUrl}
                posterStatus={posterStatus}
                displayName={displayName}
                onRegenerated={onLimitChanged}
              />
            )}
          </div>

          {/* Action buttons */}
          <div className="shrink-0 flex flex-col gap-2 items-end">
            {copyState === "copied" ? (
              <span
                role="status"
                aria-label="链接已复制到剪贴板"
                className="inline-flex items-center gap-1.5 rounded-lg bg-success-light px-3 py-2 text-sm font-medium text-accent-dark"
              >
                <Check className="h-4 w-4" aria-hidden="true" />
                已复制
              </span>
            ) : (
              <button
                type="button"
                onClick={handleCopy}
                aria-label={`复制 ${displayName} 的邀请链接`}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-foreground shadow-sm transition-colors duration-200 hover:bg-background hover:border-primary-light focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
              >
                <Copy className="h-4 w-4" aria-hidden="true" />
                复制
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowQrDialog(true)}
              aria-label={`查看 ${displayName} 的邀请二维码`}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-muted shadow-sm transition-colors duration-200 hover:bg-background hover:border-primary-light hover:text-foreground"
            >
              <QrCode className="h-3.5 w-3.5" aria-hidden="true" />
              二维码
            </button>
            <button
              type="button"
              onClick={() => setShowSuccessQrDialog(true)}
              aria-label={`设置 ${displayName} 的成功二维码`}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border bg-surface px-3 py-2 text-xs font-medium shadow-sm transition-colors duration-200 hover:bg-background hover:border-primary-light hover:text-foreground ${
                successQrUrl ? "border-accent text-accent-dark" : "border-border text-muted"
              }`}
            >
              <Image className="h-3.5 w-3.5" aria-hidden="true" />
              {successQrUrl ? "已设置" : "成功图"}
            </button>
            <button
              type="button"
              onClick={() => setShowLimitDialog(true)}
              aria-label={`设置 ${displayName} 的报名上限`}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-muted shadow-sm transition-colors duration-200 hover:bg-background hover:border-primary-light hover:text-foreground"
            >
              <Settings className="h-3.5 w-3.5" aria-hidden="true" />
              设置上限
            </button>
          </div>
        </div>
      </div>

      {showQrDialog && (
        <QrCodeDialog
          url={url}
          displayName={displayName}
          onClose={() => setShowQrDialog(false)}
        />
      )}

      {showLimitDialog && (
        <LimitDialog
          inviterId={inviterId}
          displayName={displayName}
          currentMaxCount={maxCount}
          currentCount={count}
          onClose={() => setShowLimitDialog(false)}
          onSaved={() => {
            setShowLimitDialog(false);
            onLimitChanged?.();
          }}
        />
      )}

      {showSuccessQrDialog && (
        <SuccessQrDialog
          inviterId={inviterId}
          displayName={displayName}
          currentUrl={successQrUrl}
          onClose={() => setShowSuccessQrDialog(false)}
          onSaved={() => {
            setShowSuccessQrDialog(false);
            onLimitChanged?.();
          }}
        />
      )}

      {showEditDialog && (
        <EditInviterDialog
          inviterId={inviterId}
          currentName={displayName}
          currentNote={note}
          onClose={() => setShowEditDialog(false)}
          onSaved={() => {
            setShowEditDialog(false);
            onLimitChanged?.();
          }}
        />
      )}
    </>
  );
}

// ─── LimitDialog ────────────────────────────────────────────────────────────

function LimitDialog({
  inviterId,
  displayName,
  currentMaxCount,
  currentCount,
  onClose,
  onSaved,
}: {
  inviterId: string;
  displayName: string;
  currentMaxCount?: number;
  currentCount: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [enabled, setEnabled] = useState(currentMaxCount !== undefined);
  const [value, setValue] = useState(
    currentMaxCount !== undefined ? String(currentMaxCount) : "10",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    setError("");
    let maxCount: number | null;

    if (!enabled) {
      maxCount = null;
    } else {
      const parsed = parseInt(value, 10);
      if (!Number.isInteger(parsed) || parsed <= 0) {
        setError("请输入大于 0 的整数");
        return;
      }
      if (parsed < currentCount) {
        setError(`上限不能小于当前已报名人数（${currentCount}）`);
        return;
      }
      maxCount = parsed;
    }

    setIsSaving(true);
    try {
      await setInviterLimit(inviterId, maxCount);
      onSaved();
    } catch {
      setError("保存失败，请稍后重试。");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="limit-dialog-title"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h3 id="limit-dialog-title" className="text-lg font-bold text-foreground">
            设置报名上限
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer text-muted hover:text-foreground transition-colors"
            aria-label="关闭"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <p className="text-sm text-muted mb-5">
          为 <span className="font-medium text-foreground">{displayName}</span> 设置最多可接受的报名人数。当前已有 <span className="font-medium text-foreground">{currentCount}</span> 人报名。
        </p>

        <div className="space-y-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
            />
            <span className="text-sm font-medium text-foreground">启用人数上限</span>
          </label>

          {enabled && (
            <div>
              <label
                htmlFor="max-count-input"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                最大报名人数
              </label>
              <input
                id="max-count-input"
                type="number"
                min={1}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full rounded-lg border border-border px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          )}

          {!enabled && (
            <p className="text-sm text-muted bg-background rounded-lg px-4 py-3">
              不启用时报名人数无限制。
            </p>
          )}

          {error && (
            <p role="alert" className="text-sm text-error">
              {error}
            </p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium text-muted hover:text-foreground transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SuccessQrDialog ─────────────────────────────────────────────────────────

function SuccessQrDialog({
  inviterId,
  displayName,
  currentUrl,
  onClose,
  onSaved,
}: {
  inviterId: string;
  displayName: string;
  currentUrl?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState(currentUrl ?? "");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError("请选择图片文件（PNG、JPG、SVG）");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError("图片大小不能超过 5MB");
      return;
    }

    setError("");
    setIsUploading(true);

    try {
      // 1. Get presigned upload URL from backend
      const { getSuccessQrUploadUrl } = await import("../api/client");
      const { uploadUrl, imageUrl } = await getSuccessQrUploadUrl(inviterId, file.type);

      // 2. Upload file directly to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      // 3. Save the image URL to DynamoDB
      await setSuccessQr(inviterId, imageUrl);
      setPreviewUrl(imageUrl);
      onSaved();
    } catch (err) {
      console.error("Upload failed:", err);
      setError("上传失败，请稍后重试。");
    } finally {
      setIsUploading(false);
    }
  }

  async function handleClear() {
    setIsSaving(true);
    setError("");
    try {
      await setSuccessQr(inviterId, null);
      setPreviewUrl("");
      onSaved();
    } catch {
      setError("清除失败，请稍后重试。");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold text-foreground">设置成功二维码</h3>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer text-muted hover:text-foreground transition-colors"
            aria-label="关闭"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <p className="text-sm text-muted mb-5">
          为 <span className="font-medium text-foreground">{displayName}</span> 设置报名成功后显示的二维码图片。用户报名成功后会看到此图片。
        </p>

        <div className="space-y-4">
          {/* Current preview */}
          {previewUrl && (
            <div className="rounded-lg border border-border p-3 bg-background">
              <p className="text-xs text-muted mb-2">当前图片</p>
              <img
                src={previewUrl}
                alt="成功二维码"
                className="max-w-[180px] mx-auto rounded"
              />
            </div>
          )}

          {/* Upload button */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="w-full cursor-pointer inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-background px-4 py-6 text-sm font-medium text-muted transition-colors hover:border-primary-light hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>上传中…</>
              ) : (
                <>
                  <Image className="h-5 w-5" aria-hidden="true" />
                  {previewUrl ? "更换图片" : "选择图片上传"}
                </>
              )}
            </button>
            <p className="mt-1.5 text-xs text-muted text-center">
              支持 PNG、JPG、SVG，最大 5MB
            </p>
          </div>

          {error && (
            <p role="alert" className="text-sm text-error">{error}</p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          {previewUrl ? (
            <button
              type="button"
              onClick={handleClear}
              disabled={isSaving || isUploading}
              className="cursor-pointer text-sm font-medium text-error hover:underline disabled:opacity-50"
            >
              清除图片
            </button>
          ) : (
            <div />
          )}
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium text-muted hover:text-foreground transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EditInviterDialog ───────────────────────────────────────────────────────

function EditInviterDialog({
  inviterId,
  currentName,
  currentNote,
  onClose,
  onSaved,
}: {
  inviterId: string;
  currentName: string;
  currentNote?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(currentName);
  const [note, setNote] = useState(currentNote ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleSave() {
    setError("");
    if (name.trim().length === 0) {
      setError("邀请人名称不能为空");
      return;
    }

    setIsSaving(true);
    try {
      await updateInviter(inviterId, {
        displayName: name.trim(),
        note: note.trim() || null,
      });
      onSaved();
    } catch {
      setError("保存失败，请稍后重试。");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-bold text-foreground">编辑邀请人</h3>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer text-muted hover:text-foreground transition-colors"
            aria-label="关闭"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="edit-name"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              邀请人名称
              <span className="text-error ml-1" aria-hidden="true">*</span>
            </label>
            <input
              id="edit-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          <div>
            <label
              htmlFor="edit-note"
              className="block text-sm font-medium text-foreground mb-1.5"
            >
              备注（仅管理员可见）
            </label>
            <textarea
              id="edit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="例如：合作伙伴渠道、负责人联系方式等"
              className="w-full rounded-lg border border-border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-error">{error}</p>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="cursor-pointer rounded-lg px-4 py-2 text-sm font-medium text-muted hover:text-foreground transition-colors disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="cursor-pointer inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSaving ? "保存中…" : "保存"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatRelativeDate(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleString("zh-CN", {
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
